/*
 * Authors: Koen, Triadi, mariushirjanu
 * Created: 05/05/2022
 */
import { ICompany, IDescribedNamedObject, IDescribedNamedRecord, IDescribedValueObject, IDocument, INamedObject, INamedValue, IProductImage, IRecord } from '../../../type_definitions/datatypes';
import { Injectable } from '@angular/core';

import { HttpDataService, IHttpDataCache } from '../../shared/http/http-data.service';

export interface IProductCategory extends IDescribedNamedObject {
  sequence:number;
  showInGA:boolean;
  generalLedgerNumber:number;
  type:string;
}

interface IProductModel extends IDescribedNamedRecord {
  vendorModelId:string;
  vendor:ICompany;
}

export interface IPropertyGroup {
  name:string;
  sequence?:number;
  values:IProperty[];
}

export interface IProperty {
  valueId:number;
  control:string;
  definitionId:number;
  name:string;
  categories:IProductCategory[];
  values:IDescribedNamedObject[];
  value?:string|number|boolean;
  units:IDescribedValueObject[];
  unit:IDescribedValueObject;
  type:string;
  valueText:string;
  valueReal:number;
  valueBoolean:boolean;
  valueList:number;
}

export interface IGlobalItem extends IRecord {
  globalPart:string;
  shortDescription:string;
  description:string;
  unit:IDescribedNamedRecord;
  price:string;
  model:IProductModel;
  vendor:ICompany;
  category:IProductCategory;
  url:string;
  subcategory:INamedObject;
  categoryDetail:string;

  properties:IPropertyGroup[];
  freeProperties:INamedValue[];
  document:IDocument;
}

export interface IProductImagesHolder {
  images:IProductImage[];
  modelImages?:IProductImage[];
}

export interface IPropertyOption {
  description:string;
  value:IPropertyOptionValue;
  group:string;
}

export interface IPropertyOptionValue {
  list:string;
  field:string;
  control:string;
  definitionId:number;
}

@Injectable()
export class GlobalProductDataService {
  private cache:IHttpDataCache<any>;

  constructor(
    private httpDataService:HttpDataService
  ) {
    this.cache = httpDataService.createCache();
  }

  public clear():void {
    this.cache.clear();
  }

  public categories():Promise<IProductCategory[]> {
    return this.cache.doGET(
      'rest/globalproduct/categories',
      undefined,
      undefined,
      'Error loading categories'
    );
  }

  public subCategories(category:string):Promise<IProductCategory[]> {
    return this.cache.doGET(
      'rest/globalproduct/subcategories',
      { category },
      undefined,
      'Error loading subcategories'
    );
  }

  public images(productId:number):Promise<IProductImagesHolder> {
    return this.httpDataService.doGET(
      `rest/globalproduct/products/${productId}/images`,
      undefined,
      undefined,
      'Error loading images'
    );
  }

  public properties():Promise<IPropertyGroup[]> {
    return this.cache.doGET(
      'rest/globalproduct/properties',
      undefined,
      undefined,
      'Error loading properties'
    );
  }

  public vendors():Promise<ICompany[]> {
    return this.cache.doGET(
      'rest/globalproduct/oems',
      undefined,
      undefined,
      'Error loading vendors'
    );
  }

  public product(productId:number):Promise<IGlobalItem> {
    return this.httpDataService.doGET(
      `rest/globalproduct/products/${productId}`,
      undefined,
      undefined,
      'Error loading globalproduct'
    );
  }

  public update(productId:number, productData:IGlobalItem):Promise<IGlobalItem> {
    return this.httpDataService.doPUT(
      `rest/globalproduct/products/${productId}`,
      undefined,
      productData,
      undefined,
      'Error saving globalproduct'
    );
  }

  public deleteImage(productId:number, imageId:number):Promise<void> {
    return this.httpDataService.doDELETE(
      `rest/globalproduct/products/${productId}/images/${imageId}`,
      undefined,
      undefined,
      `Unable to delete image: ${imageId} from product ${productId}`
    );
  }

  public saveImages(productId:number, imagesOrder:IProductImagesHolder):Promise<IProductImagesHolder> {
    return this.httpDataService.doPUT(
      `rest/globalproduct/products/${productId}/images`,
      undefined,
      imagesOrder,
      undefined,
      `Can't save the new image order`
    );
  }

  public isLoadingDone():Promise<boolean> {
    return this.cache.isLoadingDone();
  }

  public newProduct(productData:IGlobalItem):Promise<IGlobalItem> {
    return this.httpDataService.doPOST(
      'rest/globalproduct/products',
      undefined,
      productData,
      undefined,
      'Error saving globalproduct'
    );
  }

  public deleteDocument(productId:number, documentId:number):Promise<void> {
    return this.httpDataService.doDELETE(
      `rest/globalproduct/product/${productId}/document/${documentId}`,
      undefined,
      undefined,
      `Unable to delete document ${documentId} from product ${productId}`
    );
  }
}


